const events = {
  clicked: 'clicked'
} as const;

/* START OF COMPILED CODE */

import UserComponent from './UserComponent';
import Phaser from 'phaser';
/* START-USER-IMPORTS */
import { app } from '../App';
/* END-USER-IMPORTS */

export default class UIButton extends UserComponent {
  constructor(gameObject: Phaser.GameObjects.Container) {
    super(gameObject);

    this.gameObject = gameObject;
    (gameObject as any)['__UIButton'] = this;

    /* START-USER-CTR-CODE */
    this.isDown = false;
    this.initHitArea = new Phaser.Geom.Rectangle();
    this.usingCustomTween = false;
    this.pulseIsPaused = false;
    /* END-USER-CTR-CODE */
  }

  static getComponent(gameObject: Phaser.GameObjects.Container): UIButton {
    return (gameObject as any)['__UIButton'];
  }

  private gameObject: Phaser.GameObjects.Container;
  public btnState: boolean = true;
  public onList: (Phaser.GameObjects.Image | Phaser.GameObjects.Container)[] = [];
  public offList: (Phaser.GameObjects.Image | Phaser.GameObjects.Container)[] = [];
  public desktopVisibleList: (Phaser.GameObjects.Image | Phaser.GameObjects.Container)[] = [];
  public mobileVisibleList: (Phaser.GameObjects.Image | Phaser.GameObjects.Container)[] = [];
  public soundOn: string = 'SndItemClick';
  public soundOff: string = 'SndItemClick';
  public shouldPulse: boolean = false;
  public pulseDelay: number = 2000;
  public pulseScaleEnd: number = 1.05;
  public pulseSpeed: number = 400;
  public pulseEase: (v: number) => number = Phaser.Math.Easing.Cubic.InOut;
  public pulseRepeat: boolean = false;
  public pulseRepeatTime: number = 6000;

  /* START-USER-CODE */
  public static events = events;

  private isDown: boolean;
  private initScaleX!: number;
  private initScaleY!: number;
  private initHitArea: Phaser.Geom.Rectangle;
  private usingCustomTween: boolean;
  private customTween: Phaser.Tweens.Tween | undefined;
  private pulseTween: Phaser.Tweens.Tween | undefined;
  private pulseRepeatEvent: Phaser.Time.TimerEvent | undefined;
  private pulseIsPaused: boolean;

  public awake() {
    this.initScaleX = this.gameObject.scaleX;
    this.initScaleY = this.gameObject.scaleY;

    this.togglePlatformVisuals();
    this.calculateHitArea();

    this.gameObject.setInteractive();
    this.gameObject.on(Phaser.Input.Events.POINTER_DOWN, this.onPointerDown, this);
    this.gameObject.on(Phaser.Input.Events.POINTER_UP, this.onPointerUpInside, this);
    this.scene.input.on(Phaser.Input.Events.POINTER_OUT, this.onPointerUpOutside, this);
    this.scene.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.onPointerUpOutside, this);

    if (this.shouldPulse) {
      this.scene.time.delayedCall(this.pulseDelay, this.pulseBtn, undefined, this);
    }
  }

  public setState(state: boolean) {
    this.updateState(state);
  }

  public ignoreCamera(camera: Phaser.Cameras.Scene2D.Camera) {
    this.gameObject.scrollFactorX = 0;
    this.gameObject.scrollFactorY = 0;
  }

  public pausePulse() {
    this.pulseIsPaused = true;

    // Tween may have finished and been garbage collected.
    if (this.pulseTween?.state != Phaser.Tweens.REMOVED) this.pulseTween?.pause();

    this.pulseRepeatEvent?.remove();
  }

  public resumePulse() {
    if (!this.pulseIsPaused) return;

    this.pulseIsPaused = false;
    this.pulseBtn();
  }

  public setCustomPulseTween(tween: Phaser.Tweens.Tween) {
    if (this.pulseTween) this.pulseTween.stop();

    this.customTween = tween;
    this.customTween.pause();
    this.customTween.restart();
    this.usingCustomTween = true;
  }

  protected calculateHitArea() {
    if (this.gameObject.list.length > 0) {
      let tempVector = new Phaser.Math.Vector2();

      for (let i = 0; i < this.gameObject.list.length; i++) {
        const element = this.gameObject.list[i];

        if (element instanceof Phaser.GameObjects.Image) {
          element.getBottomLeft(tempVector);
          if (tempVector.x < this.initHitArea.left) this.initHitArea.left = tempVector.x;
          if (tempVector.y > this.initHitArea.bottom) this.initHitArea.bottom = tempVector.y;

          element.getTopRight(tempVector);
          if (tempVector.x > this.initHitArea.right) this.initHitArea.right = tempVector.x;
          if (tempVector.y < this.initHitArea.top) this.initHitArea.top = tempVector.y;
        }
      }

      this.initHitArea.setPosition(0, 0);
      this.gameObject.setSize(this.initHitArea.width, this.initHitArea.height);
    }
  }

  protected pulseBtn() {
    if (!this.shouldPulse) return;

    // Tween may have finished and been garbage collected even if paused.
    if (this.pulseTween == undefined || this.pulseTween?.state == Phaser.Tweens.REMOVED) {
      if (this.usingCustomTween) {
        this.pulseTween = this.customTween;
      } else {
        this.pulseTween = this.scene.add.tween({
          targets: this.gameObject,
          scaleX: { from: this.initScaleX, to: this.pulseScaleEnd },
          scaleY: { from: this.initScaleY, to: this.pulseScaleEnd },
          duration: this.pulseSpeed,
          ease: Phaser.Math.Easing.Cubic.InOut,
          loop: -1,
          yoyo: true,
          paused: true
        });
      }

      this.pulseTween?.play();
    } else {
      if (this.pulseTween.isPaused()) this.pulseTween.resume();

      this.pulseTween?.restart();
      this.pulseTween?.play();
    }

    if (this.pulseRepeat) {
      this.pulseRepeatEvent = this.scene.time.delayedCall(this.pulseRepeatTime, this.pulseBtn, undefined, this);
    }
  }

  protected onPointerDown() {
    this.isDown = true;

    if (this.pulseTween) {
      this.pausePulse();
    }

    this.scene.add.tween({
      targets: this.gameObject,
      scaleX: this.initScaleX * 0.8,
      scaleY: this.initScaleY * 0.8,
      duration: 80,
      // yoyo: true,
      onStart: () => {
        if (this.gameObject.input.hitArea instanceof Phaser.Geom.Rectangle) {
          // console.log('hitarea start', this.gameObject.input.hitArea);
          let diffWidth = this.initHitArea.width * (1 / 0.8) - this.initHitArea.width;
          let diffHeight = this.initHitArea.height * (1 / 0.8) - this.initHitArea.height;
          this.gameObject.input.hitArea.setTo(-diffWidth * 0.5, -diffHeight * 0.5, this.initHitArea.width + diffWidth, this.initHitArea.height + diffHeight);
          // console.log('hitarea start', this.gameObject.input.hitArea);
        }
      }
    });
  }

  protected onPointerUpInside() {
    this.isDown = false;
    app.audioHandler.playSound(this.scene, this.btnState ? this.soundOn : this.soundOff, 'sfx');
    this.updateState(!this.btnState);

    this.scene.add.tween({
      targets: this.gameObject,
      scaleX: this.initScaleX,
      scaleY: this.initScaleY,
      duration: 80,
      ease: Phaser.Math.Easing.Cubic.InOut,
      // yoyo: true,
      onComplete: () => {
        this.gameObject.emit(UIButton.events.clicked);

        if (this.gameObject.input.hitArea instanceof Phaser.Geom.Rectangle) {
          // console.log('hitarea up', this.gameObject.input.hitArea);
          this.gameObject.input.hitArea.setTo(this.initHitArea.x, this.initHitArea.y, this.initHitArea.width, this.initHitArea.height);
          // console.log('hitarea up', this.gameObject.input.hitArea);
        }

        if (this.pulseTween && this.pulseIsPaused) {
          this.resumePulse();
        }
      }
    });
  }

  protected onPointerUpOutside() {
    // We only trigger if we were pressed and then they released outside.
    if (!this.isDown) return;
    this.isDown = false;

    this.scene.add.tween({
      targets: this.gameObject,
      scaleX: this.initScaleX,
      scaleY: this.initScaleY,
      duration: 80,
      ease: Phaser.Math.Easing.Cubic.InOut,
      // yoyo: true,
      onComplete: () => {
        if (this.gameObject.input.hitArea instanceof Phaser.Geom.Rectangle) {
          // console.log('hitarea out', this.gameObject.input.hitArea);
          this.gameObject.input.hitArea.setTo(this.initHitArea.x, this.initHitArea.y, this.initHitArea.width, this.initHitArea.height);
          // console.log('hitarea out', this.gameObject.input.hitArea);
        }

        if (this.pulseTween && this.pulseIsPaused) {
          this.resumePulse();
        }

        // if (this.pulseTween && this.pulseTween.isPaused()) {
        //   this.pulseTween.restart();
        //   if (this.pulseTween.isPaused()) this.pulseTween.play();
        // }
      }
    });
  }

  protected updateState(newState: boolean) {
    this.btnState = newState;
    this.onList.forEach((object) => object.setVisible(newState));
    this.offList.forEach((object) => object.setVisible(!newState));
  }

  protected togglePlatformVisuals() {
    this.desktopVisibleList.forEach((obj) => {
      obj.setActive(this.scene.sys.game.device.os.desktop);
      obj.setVisible(this.scene.sys.game.device.os.desktop);
    });
    this.mobileVisibleList.forEach((obj) => {
      obj.setActive(!this.scene.sys.game.device.os.desktop);
      obj.setVisible(!this.scene.sys.game.device.os.desktop);
    });
  }

  protected destroy() {
    this.gameObject.off(Phaser.Input.Events.POINTER_DOWN, this.onPointerDown, this);
    this.gameObject.off(Phaser.Input.Events.POINTER_UP, this.onPointerUpInside, this);
    this.scene.input.off(Phaser.Input.Events.POINTER_OUT, this.onPointerUpOutside, this);
    this.scene.input.off(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.onPointerUpOutside, this);
    this.pulseTween = undefined;
    this.pulseRepeatEvent = undefined;

    super.destroy();
  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
